import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { getRepairCustomerProfile } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDateTime, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const fmtWhen = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const statusColor = (status) => {
  const map = {
    pending: 'warning',
    diagnosis: 'info',
    quoted: 'primary',
    approved: 'primary',
    in_progress: 'secondary',
    completed: 'success',
    delivered: 'success',
    cancelled: 'error'
  };
  return map[status] || 'default';
};

const RepairCustomerProfile = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRepairCustomerProfile(customerId);
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load customer profile'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <CircularProgress />;
  if (error && !data) return <Alert severity="error">{error}</Alert>;

  const c = data.customer || {};
  const s = data.summary || {};
  const items = data.items || [];
  const jobs = data.jobs || [];
  const repeats = items.filter((i) => i.visit_count > 1);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/repairs/customers')} sx={{ mb: 2 }}>
        All customers
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{c.name || 'Customer'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[c.customer_code, c.phone, c.email, c.company_name].filter(Boolean).join(' · ') || 'No contact details'}
          </Typography>
          {c.address && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{c.address}</Typography>
          )}
        </Box>
        <ExportButtons
          title={`Customer ${c.name || customerId}`}
          filename={`repair_customer_${customerId}`}
          columns={[
            { label: 'JOB', value: (r) => dash(r.grn_number || r.service_number) },
            { label: 'Date', value: (r) => fmtExportDateTime(r.created_at) },
            { label: 'Device', value: (r) => dash([r.device_brand, r.device_model, r.device_type].filter(Boolean).join(' ')) },
            { label: 'Serial', value: (r) => dash(r.device_serial || r.device_imei) },
            { label: 'Issue', value: (r) => dash(r.device_issue) },
            { label: 'Status', value: (r) => fmtExportStatus(r.status) },
            { label: 'Amount', value: (r) => fmtExportMoney(r.total_amount) }
          ]}
          rows={jobs}
        />
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Total jobs', value: s.total_jobs || 0 },
          { label: 'Open', value: s.open_jobs || 0 },
          { label: 'Closed', value: s.closed_jobs || 0 },
          { label: 'Warranty', value: s.warranty_jobs || 0 },
          { label: 'Billed', value: money(s.total_cost) },
          { label: 'Paid', value: money(s.total_paid) },
          { label: 'Balance', value: money(s.balance_due) },
          { label: 'Same item returned', value: s.repeat_items || 0 }
        ].map((card) => (
          <Grid item xs={6} sm={4} md={3} key={card.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        First job {fmtWhen(s.first_job_at)} · Last job {fmtWhen(s.last_job_at)}
        {s.parts_cost ? ` · Parts ${money(s.parts_cost)}` : ''}
        {s.labor_cost ? ` · Labor ${money(s.labor_cost)}` : ''}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Items brought in
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Same serial / IMEI / model counted as the same item. Highlighted rows came more than once.
        </Typography>
        {items.length === 0 ? (
          <Typography color="text.secondary">No devices on file.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Serial / IMEI</TableCell>
                <TableCell align="right">Times in</TableCell>
                <TableCell align="right">Spend</TableCell>
                <TableCell>First / last</TableCell>
                <TableCell>Latest issue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.key} sx={{ bgcolor: it.visit_count > 1 ? 'action.selected' : undefined }}>
                  <TableCell>
                    <Typography fontWeight={600}>{it.label}</Typography>
                    {it.visit_count > 1 && <Chip size="small" color="warning" label="Repeat" sx={{ mt: 0.5 }} />}
                  </TableCell>
                  <TableCell>{it.device_serial || it.device_imei || '—'}</TableCell>
                  <TableCell align="right">{it.visit_count}</TableCell>
                  <TableCell align="right">{money(it.total_cost)}</TableCell>
                  <TableCell>
                    {fmtWhen(it.first_visit)}
                    {it.visit_count > 1 ? ` → ${fmtWhen(it.last_visit)}` : ''}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 280 }} noWrap title={it.issues?.[0]}>
                      {it.issues?.[0] || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {repeats.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {repeats.length} item{repeats.length === 1 ? '' : 's'} came back more than once.
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>All repair jobs</Typography>
        <Divider sx={{ mb: 1 }} />
        {jobs.length === 0 ? (
          <Typography color="text.secondary">No jobs for this customer.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>JOB</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Issue / diagnosis</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {j.grn_number || j.service_number || `#${j.id}`}
                  </TableCell>
                  <TableCell>{fmtWhen(j.received_date || j.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {[j.device_brand, j.device_model].filter(Boolean).join(' ') || j.device_type || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[j.device_type, j.device_serial || j.device_imei].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 260 }} noWrap title={j.device_issue}>
                      {j.device_issue || '—'}
                    </Typography>
                    {j.diagnosis && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap title={j.diagnosis}>
                        {j.diagnosis}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={statusColor(j.status)} label={(j.status || '').replace(/_/g, ' ')} />
                    {j.service_coverage === 'warranty' && (
                      <Chip size="small" color="success" label="Warranty" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">{money(j.total_amount)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/repairs/${j.id}`)}>View job</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default RepairCustomerProfile;
